﻿import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { Plus } from "lucide-react"
import * as React from "react"

import { ExportButton } from "@/features/exports/export-button"
import { DataTable, useListParams, type Column } from "@/components/data-table"
import { Badge, Button, Label, PageHeader, Select } from "@/components/ui"
import { PAGE_SIZE, STATUS_LABELS, STATUS_TONES } from "@/lib/constants"
import { useSession } from "@/lib/session"

import {
  INTERVENTION_STATUSES,
  useCreateIntervention,
  useInterventionList,
  type Intervention,
  type InterventionIn,
  type InterventionOrdering,
  type InterventionStatus,
} from "@/features/interventions/api"
import { InterventionEditorModal } from "@/features/interventions/editor-modal"
import {
  NO_ERRORS,
  submitErrorsFrom,
  type SubmitErrors,
} from "@/features/interventions/validation"

export const Route = createFileRoute("/interventions/")({
  component: InterventionsScreen,
})

function InterventionsScreen() {
  const navigate = useNavigate()
  const { canWrite } = useSession()
  const { page, setPage, ordering, setOrdering, search, setSearch, debouncedSearch } =
    useListParams<InterventionOrdering>("name")
  const [status, setStatusState] = React.useState<InterventionStatus | "">("")

  // A narrower result set rarely has the page you were on, so filtering
  // restarts at page 1, exactly as searching and sorting do.
  function setStatus(value: InterventionStatus | "") {
    setStatusState(value)
    setPage(1)
  }

  const query = useInterventionList({ page, q: debouncedSearch, status, ordering })

  const create = useCreateIntervention()
  const [creating, setCreating] = React.useState(false)
  const [createErrors, setCreateErrors] = React.useState<SubmitErrors>(NO_ERRORS)

  function submitCreate(payload: InterventionIn) {
    setCreateErrors(NO_ERRORS)
    create.mutate(payload, {
      onSuccess: (created) => {
        setCreating(false)
        // The id comes from the server. Nothing here invents one.
        if (created?.id != null) {
          void navigate({
            to: "/interventions/$interventionId",
            params: { interventionId: String(created.id) },
          })
        }
      },
      onError: (cause) => setCreateErrors(submitErrorsFrom(cause)),
    })
  }

  const columns: Column<Intervention>[] = [
    {
      key: "name",
      header: "Name",
      sortable: true,
      primary: true,
      cell: (row) => <span className="font-medium">{row.name}</span>,
    },
    {
      key: "training_type",
      header: "Type",
      sortable: true,
      cell: (row) => row.training_type || <Absent />,
    },
    {
      key: "training_category",
      header: "Category",
      sortable: true,
      cell: (row) => row.training_category || <Absent />,
    },
    {
      key: "nqf_level",
      header: "NQF",
      sortable: true,
      cell: (row) => row.nqf_level || <Absent />,
    },
    {
      // Not sortable: `credits` is not one of the orderings the endpoint
      // accepts, and sorting the 50 rows on screen would sort the wrong set.
      key: "credits",
      header: "Credits",
      cell: (row) => (row.credits == null ? <Absent /> : row.credits),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      cell: (row) => (
        <Badge tone={STATUS_TONES[row.status] ?? "neutral"}>
          {STATUS_LABELS[row.status] ?? row.status}
        </Badge>
      ),
    },
  ]

  const newButton = canWrite ? (
    <Button
      onClick={() => {
        setCreateErrors(NO_ERRORS)
        setCreating(true)
      }}
    >
      <Plus className="h-4 w-4" aria-hidden />
      New intervention
    </Button>
  ) : undefined

  return (
    <>
      <PageHeader
        eyebrow="Libraries"
        title="Interventions"
        description="The catalogue of courses available to schedule as training events."
        actions={
          <div className="flex items-start gap-2">
            <ExportButton
              slug="interventions"
              params={{ q: debouncedSearch, status, ordering }}
            />
            {newButton}
          </div>
        }
      />
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        count={query.data?.count ?? 0}
        rowKey={(row) => row.id ?? row.name}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={setPage}
        ordering={ordering}
        onOrderingChange={setOrdering}
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search interventions by name"
        isLoading={query.isPending}
        isRefreshing={query.isFetching && !query.isPending}
        isError={query.isError}
        onRetry={() => void query.refetch()}
        onRowClick={(row) => {
          if (row.id == null) return
          void navigate({
            to: "/interventions/$interventionId",
            params: { interventionId: String(row.id) },
          })
        }}
        emptyTitle={
          debouncedSearch || status ? "No interventions match" : "No interventions yet"
        }
        emptyDescription={
          debouncedSearch || status
            ? "Try a different search, or clear the status filter."
            : "The catalogue is empty. Add the first intervention to start scheduling events."
        }
        emptyAction={newButton}
        toolbar={
          <div className="flex items-center gap-2">
            <Label htmlFor="status-filter" className="mb-0 whitespace-nowrap text-sm">
              Status
            </Label>
            <Select
              id="status-filter"
              value={status}
              onChange={(event) =>
                setStatus(event.target.value as InterventionStatus | "")
              }
              className="w-40"
            >
              <option value="">All</option>
              {INTERVENTION_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {STATUS_LABELS[value] ?? value}
                </option>
              ))}
            </Select>
          </div>
        }
      />

      {creating && (
        <InterventionEditorModal
          intervention={null}
          errors={createErrors}
          pending={create.isPending}
          onSubmit={submitCreate}
          onClose={() => setCreating(false)}
        />
      )}
    </>
  )
}

/** An empty cell still needs to read as empty, not as a rendering failure. */
function Absent() {
  return (
    <span className="text-muted-foreground" aria-label="Not recorded">
      &ndash;
    </span>
  )
}
